import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@repo/ui/Button';
import { NavigationHeader } from '../../components/NavigationHeader/NavigationHeader';
import { Layout } from '../../components/Layout/Layout';
import { useItemResolutions } from '../../hooks/useItemResolutions';
import { NavigationFooter } from '../../components/NavigationFooter/NavigationFooter';
import { Text } from '../../components/Language/Language';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import { useItems } from '../../hooks/useItems';
import { ExchangeDetails } from './ExchangeDetails';
import { useWorkflow } from '../../workflow/hooks';
import { updateWorkflowOnBack } from './helpers';
import { ItemCard } from '../../components/ItemCard/ItemCard';
import { getItemDescription } from '../../utils/item';
import { Flex } from '@repo/ui/Flex';
import s from './ExchangeLayout.module.css';
import { Page } from '../../components/Page/Page';

const AttributeFormSchema = z.object({
  attributes: z.record(z.string(), z.string()),
  exchangeItem: z.object({
    variantId: z.string(),
    productId: z.string(),
  }),
});

export type AttributeFormSchemaType = z.infer<typeof AttributeFormSchema>;

export const ExchangeLayout = () => {
  const router = useRouter();
  const params = useSearch({ strict: false });
  const methods = useForm<AttributeFormSchemaType>({
    resolver: zodResolver(AttributeFormSchema),
    defaultValues: { attributes: {} },
  });
  const navigate = useNavigate();
  const { data, updateData, retailer } = useWorkflow();
  const isMobile = useIsMobile();
  const itemId = params?.itemId;
  const items = useItems();
  const item = items.find((i) => i.id === itemId);
  const resolutionId = params?.resolutionId;
  const { resolutionOptions } = useItemResolutions(itemId);
  const resolutionData = resolutionOptions?.find((o) => o.id === resolutionId);
  const exchanges = resolutionData?.exchanges;

  const handleBack = () => {
    updateData(updateWorkflowOnBack({ item, data }));
    router.history.back();
  };

  const handleSubmit = methods.handleSubmit((formData) => {
    if (!data?.items || !item) {
      throw new Error('Cannot resolve without item data');
    }

    updateData({
      ...data,
      items: {
        ...data.items,
        [item.id]: {
          ...data.items?.[item.id],
          resolution: {
            resolutionId,
            exchangeItem: formData.exchangeItem,
          },
        },
      },
    });

    navigate({
      to: '/$retailer/reasons',
      params: { retailer: retailer ?? 's' },
      search: (prev) => ({
        ...prev,
        itemId: undefined,
        resolutionId: undefined,
      }),
    });
  });

  if (!exchanges || !item) return <div>Something went wrong</div>;

  return (
    <Page>
      <NavigationHeader
        title={{
          langKey: 'Global.exchange.page.header',
        }}
        onClose={handleBack}
        leftChildren={
          !isMobile && (
            <ItemCard
              className={s.itemCardWrapper}
              imageSize="sm"
              image={item.image?.url ?? ''}
              padding="p-8"
              margin="m-8"
            >
              <Flex direction="column" justifyContent="space-evenly">
                <strong className={s.itemTitle}>{item.name}</strong>
                <div className={s.itemDescription}>
                  {getItemDescription({ item })}
                </div>
              </Flex>
            </ItemCard>
          )
        }
      />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <Layout>
            {isMobile && (
              <ItemCard
                className={s.itemCardWrapper}
                imageSize="sm"
                image={item.image?.url ?? ''}
                padding="p-8"
                margin={['mx-16', 'mb-24']}
              >
                <Flex direction="column" justifyContent="space-evenly">
                  <strong className={s.itemTitle}>{item.name}</strong>
                  <div className={s.itemDescription}>
                    {getItemDescription({ item: { variant: item.variant } })}
                  </div>
                </Flex>
              </ItemCard>
            )}
            {exchanges.sameProduct && (
              <ExchangeDetails
                item={item}
                productInfo={exchanges.sameProduct}
              />
            )}
            <NavigationFooter
              leftChildren={
                !isMobile && (
                  <Button onClick={handleBack} variant="secondary">
                    <Text langKey="Global.back" />
                  </Button>
                )
              }
              rightChildren={
                <Button
                  type="submit"
                  variant="primary"
                  isFullWidth={isMobile}
                  disabled={!methods.formState.isValid}
                  onClick={handleSubmit}
                >
                  <Text langKey="Global.exchange.page.submit" />
                </Button>
              }
            />
          </Layout>
        </form>
      </FormProvider>
    </Page>
  );
};
